/**
 * The token-free retail BFF session client (KAN-2036, epic KAN-2030).
 *
 * The SIGN-IN CEREMONY itself is NOT here — that is fully reused from the shared
 * `<DhruvantaAuth config={{ posture: 'bff' }}>` card in `@dhruvanta/ui/react`, which
 * drives the standard `/idp/bff/*` endpoints through the package's own
 * `BffTransport`. This module is only the three SESSION-LIFECYCLE legs the app shell
 * needs around that card:
 *
 *   probeBffSession()   boot: "is there a live Dhruvanta companion session?"
 *   silentReseat()      401 recovery: re-seat the retail cookie server-side
 *   bffLogout()         sign-out: clear the sealed server-side container
 *
 * WHY NOT REUSE `BffTransport` FOR THESE TOO: it exposes no `refresh()`, and its
 * rotating CSRF handle is a private field, so the silent re-seat cannot be driven
 * through it. Importing `@dhruvanta/ui/react` here would also couple the SERVICE
 * layer to the React design system for three fetch calls. The ceremony — the part
 * that actually matters for security — is 100% shared; this is a ~100-line
 * lifecycle shim over the same documented wire contract.
 *
 * CROWN-JEWEL INVARIANT — this module NEVER reads or writes localStorage /
 * sessionStorage / IndexedDB and NEVER holds a token. The rotating CSRF handle
 * lives in a MODULE-LOCAL variable (memory, not web storage) and is NOT a token: it
 * authorises state-changing BFF routes, it does not authorise any API. Every IdP
 * access / refresh / id token stays sealed server-side in the httpOnly
 * `RETAIL_<PORTAL>_BFF` cookie. `tests/tokenFreeInvariant.test.ts` fails the build
 * if that regresses.
 */

import { DHRUVANTA_BFF_BASE_PATH } from '../config/dhruvantaAuth'

/** The non-secret user claims the broker surfaces. Mirrors the server-side BffUser. */
export interface RetailBffUser {
  sub?: string
  email?: string
  name?: string
  roles?: string[]
}

/** An established / restored BFF companion session. Claims only — NEVER a token. */
export interface RetailBffSession {
  authenticated: true
  user: RetailBffUser
  expiresAt?: number | null
}

/**
 * A typed BFF failure. `code` is the broker's machine code — the SPA branches on it
 * rather than on message text.
 *
 * THE 401 vs 403 CONTRACT (see `api/idp/brokerConfig.ts#mapBridgeError`):
 *   401 → unauthenticated. Safe to treat as "no session"; the caller may redirect
 *         to sign-in.
 *   403 → AUTHENTICATED BUT NOT AUTHORISED (e.g. `portal_forbidden`, `csrf_mismatch`).
 *         MUST surface inline. It must NEVER trigger a logout or clear a session —
 *         doing exactly that destroyed valid production sessions on PrintAnywhere.
 */
export class RetailBffError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string) {
    super(code)
    this.name = 'RetailBffError'
    this.status = status
    this.code = code
  }

  /** True for "not signed in" — the only class that may bounce the user to sign-in. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** True for "signed in, but not allowed here" — ALWAYS surfaced inline. */
  get isForbidden(): boolean {
    return this.status === 403
  }
}

/**
 * The rotating per-session CSRF handle, held in MEMORY only (never web storage).
 * Minted by the broker on `/session` and rotated on every sealed-container write.
 * Not a token.
 */
let csrfHandle: string | null = null

/** Test seam + sign-out hygiene: forget the in-memory handle. */
export function resetBffCsrfHandle(): void {
  csrfHandle = null
}

interface BffCall {
  method: 'GET' | 'POST'
  body?: unknown
  /** Echo the in-memory CSRF handle (required on every state-changing route). */
  csrf?: boolean
}

async function bffFetch(path: string, init: BffCall): Promise<unknown> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (init.body !== undefined) headers['Content-Type'] = 'application/json'
  if (init.csrf && csrfHandle) headers['X-Bff-Csrf'] = csrfHandle

  let response: Response
  try {
    response = await fetch(`${DHRUVANTA_BFF_BASE_PATH}${path}`, {
      method: init.method,
      headers,
      // Same-origin: the httpOnly sealed BFF cookie rides automatically and can
      // never be read by script. `same-origin` (not `include`) is deliberate — the
      // broker is mounted on this very origin, so there is no cross-site leg at all.
      credentials: 'same-origin',
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    })
  } catch {
    throw new RetailBffError(0, 'network_error')
  }

  let json: unknown = null
  try {
    json = await response.json()
  } catch {
    json = null
  }

  if (!response.ok) {
    const code =
      json && typeof json === 'object' && typeof (json as { error?: unknown }).error === 'string'
        ? (json as { error: string }).error
        : `http_${response.status}`
    throw new RetailBffError(response.status, code)
  }

  if (json && typeof json === 'object' && typeof (json as { csrf?: unknown }).csrf === 'string') {
    csrfHandle = (json as { csrf: string }).csrf
  }
  return json
}

/**
 * BOOT PROBE — `GET /idp/bff/session`. Resolves the sealed companion's claims, or
 * `null` when there is no live Dhruvanta session (the broker answers 401
 * `login_required`, which is NOT an error for an anonymous storefront visitor).
 * Also captures the CSRF handle the later state-changing legs need.
 */
export async function probeBffSession(): Promise<RetailBffSession | null> {
  try {
    const json = (await bffFetch('/session', { method: 'GET' })) as RetailBffSession
    return json?.authenticated ? json : null
  } catch (error) {
    if (error instanceof RetailBffError && error.isUnauthenticated) return null
    throw error
  }
}

/**
 * SILENT RE-SEAT — `POST /idp/bff/refresh`. Rotates the app-scoped token set
 * SERVER-SIDE and re-calls the retail bridge so the retail session cookie is seated
 * again, all without a single token touching the browser. Returns `true` when a
 * session is live afterwards.
 *
 * Used exactly once per boot, after a retail `/me` 401, so there is no possible
 * boot → re-seat → 401 → re-seat loop.
 */
export async function silentReseat(variant: string): Promise<boolean> {
  // The CSRF handle is minted by /session; without it /refresh answers 403
  // csrf_mismatch. Probe first when we do not hold one yet.
  if (!csrfHandle) {
    const probed = await probeBffSession().catch(() => null)
    if (!probed) return false
  }
  try {
    const json = (await bffFetch('/refresh', {
      method: 'POST',
      body: { variant },
      csrf: true,
    })) as RetailBffSession
    return json?.authenticated === true
  } catch (error) {
    // 401 → the sealed family is genuinely gone. 403 → NOT a dead session (a stale
    // CSRF handle is the usual cause); either way we simply report "not re-seated"
    // and let the caller show sign-in. We never destroy state here.
    if (error instanceof RetailBffError) return false
    throw error
  }
}

/**
 * SIGN OUT — `POST /idp/bff/logout`. Clears the whole sealed server-side container
 * (every remembered account). Best-effort: the retail-native `POST /auth/logout`
 * clears the retail session cookie regardless, so a broker hiccup must never leave
 * the user stuck "signed in".
 */
export async function bffLogout(): Promise<void> {
  try {
    await bffFetch('/logout', { method: 'POST', body: {}, csrf: true })
  } catch {
    // Swallowed on purpose — see the note above.
  } finally {
    resetBffCsrfHandle()
  }
}
